/** 2026 CFA® Level 1 exam structure. Source: CFA Institute Level 1 exam page and 2026 topic outlines. This is not CFA Institute curriculum. */

export const SYLLABUS_YEAR = 2026;

export const EXAM_FORMAT = {
  items: 180,
  sessions: 2,
  itemsPerSession: 90,
  minutesPerSession: 135,
  choices: 3,
  calculator: 'Texas Instruments BA II Plus or Hewlett Packard 12C',
  fsaBasis: 'IFRS unless a question states US GAAP',
};

export const OFFICIAL_OUTLINES_URL =
  'https://www.cfainstitute.org/programs/cfa-program/candidate-resources/level-i-exam';

export const SYLLABUS_NOTE =
  'Level 1 2026 uses the same ten topics, weights, and learning modules as 2025 (93 modules). A Practical Skills Module is required to receive an exam result.';

/** Official exam-weight ranges and session (1 = morning, 2 = afternoon). */
export const TOPIC_WEIGHTS_2026 = {
  'Ethical and Professional Standards': { min: 0.15, max: 0.2, session: 1 },
  'Quantitative Methods': { min: 0.06, max: 0.09, session: 1 },
  Economics: { min: 0.06, max: 0.09, session: 1 },
  'Financial Reporting and Analysis': { min: 0.11, max: 0.14, session: 1 },
  'Corporate Issuers': { min: 0.06, max: 0.09, session: 1 },
  'Equity Investments': { min: 0.11, max: 0.14, session: 2 },
  'Fixed Income': { min: 0.11, max: 0.14, session: 2 },
  Derivatives: { min: 0.05, max: 0.08, session: 2 },
  'Alternative Investments': { min: 0.07, max: 0.1, session: 2 },
  'Portfolio Management and Wealth Planning': { min: 0.08, max: 0.12, session: 2 },
};

export function formatWeightRange(topicFirestoreName) {
  const w = TOPIC_WEIGHTS_2026[topicFirestoreName];
  if (!w) return '';
  return `${Math.round(w.min * 100)}–${Math.round(w.max * 100)}%`;
}

export const LEARNING_MODULES_2026 = {
  'Ethical and Professional Standards': [
    'Ethics and Trust in the Investment Profession',
    'Code of Ethics and Standards of Professional Conduct',
    'Guidance for Standards I–VII',
    'Introduction to the Global Investment Performance Standards (GIPS)',
    'Ethics Application',
  ],
  'Quantitative Methods': [
    'Rates and Returns',
    'Time Value of Money in Finance',
    'Statistical Measures of Asset Returns',
    'Probability Trees and Conditional Expectations',
    'Portfolio Mathematics',
    'Simulation Methods',
    'Estimation and Inference',
    'Hypothesis Testing',
    'Parametric and Non-Parametric Tests of Independence',
    'Simple Linear Regression',
    'Introduction to Big Data Techniques',
  ],
  Economics: [
    'The Firm and Market Structures',
    'Understanding Business Cycles',
    'Fiscal Policy',
    'Monetary Policy',
    'Introduction to Geopolitics',
    'International Trade',
    'Capital Flows and the FX Market',
    'Exchange Rate Calculations',
  ],
  'Corporate Issuers': [
    'Organizational Forms, Corporate Issuer Features, and Ownership',
    'Investors and Other Stakeholders',
    'Corporate Governance: Conflicts, Mechanisms, Risks, and Benefits',
    'Working Capital and Liquidity',
    'Capital Investments and Capital Allocation',
    'Capital Structure',
    'Business Models',
  ],
  'Financial Reporting and Analysis': [
    'Introduction to Financial Statement Analysis',
    'Analyzing Income Statements',
    'Analyzing Balance Sheets',
    'Analyzing Statements of Cash Flows I',
    'Analyzing Statements of Cash Flows II',
    'Analysis of Inventories',
    'Analysis of Long-Term Assets',
    'Topics in Long-Term Liabilities and Equity',
    'Analysis of Income Taxes',
    'Financial Reporting Quality',
    'Financial Analysis Techniques',
    'Introduction to Financial Statement Modeling',
  ],
  'Equity Investments': [
    'Market Organization and Structure',
    'Security Market Indexes',
    'Market Efficiency',
    'Overview of Equity Securities',
    'Company Analysis: Past and Present',
    'Industry and Competitive Analysis',
    'Company Analysis: Forecasting',
    'Equity Valuation: Concepts and Basic Tools',
  ],
  'Fixed Income': [
    'Fixed-Income Instrument Features',
    'Fixed-Income Cash Flows and Types',
    'Fixed-Income Issuance and Trading',
    'Fixed-Income Markets for Corporate Issuers',
    'Fixed-Income Markets for Government Issuers',
    'Fixed-Income Bond Valuation: Prices and Yields',
    'Yield and Yield Spread Measures for Fixed-Rate Bonds',
    'Yield and Yield Spread Measures for Floating-Rate Instruments',
    'The Term Structure of Interest Rates',
    'Interest Rate Risk and Return',
    'Yield-Based Bond Duration Measures and Properties',
    'Yield-Based Bond Convexity and Portfolio Properties',
    'Curve-Based and Empirical Fixed-Income Risk Measures',
    'Credit Risk',
    'Credit Analysis for Government Issuers',
    'Credit Analysis for Corporate Issuers',
    'Fixed-Income Securitization',
    'ABS Instrument and Market Features',
    'MBS Instrument and Market Features',
  ],
  Derivatives: [
    'Derivative Instrument and Derivative Market Features',
    'Forward Commitment and Contingent Claim Features and Instruments',
    'Derivative Benefits, Risks, and Issuer and Investor Uses',
    'Arbitrage, Replication, and the Cost of Carry',
    'Pricing and Valuation of Forward Contracts',
    'Pricing and Valuation of Futures Contracts',
    'Pricing and Valuation of Interest Rates and Other Swaps',
    'Pricing and Valuation of Options',
    'Option Replication Using Put–Call Parity',
    'Valuing a Derivative Using a One-Period Binomial Model',
  ],
  'Alternative Investments': [
    'Alternative Investment Features, Methods, and Structures',
    'Alternative Investment Performance and Returns',
    'Investments in Private Capital: Equity and Debt',
    'Real Estate and Infrastructure',
    'Natural Resources',
    'Hedge Funds',
    'Introduction to Digital Assets',
  ],
  'Portfolio Management and Wealth Planning': [
    'Portfolio Risk and Return: Part I',
    'Portfolio Risk and Return: Part II',
    'Portfolio Management: An Overview',
    'Basics of Portfolio Planning and Construction',
    'The Behavioral Biases of Individuals',
    'Introduction to Risk Management',
  ],
};

export const LEARNING_MODULE_COUNT = Object.values(LEARNING_MODULES_2026).reduce(
  (n, modules) => n + modules.length,
  0
);
